import type { DocumentV1 } from '@alpod-ai-writer/backend';
import { JobQueueStatus } from '@alpod-ai-writer/backend/src/db/schema';
import type { JobQueueV1 } from '@alpod-ai-writer/backend/src/schemas/jobQueue/jobQueue.schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangleIcon, CheckIcon, ClockIcon, LoaderCircleIcon } from 'lucide-react';

import { useApi } from '../../../api/useApi.hook';
import { useDocumentJobs } from '../../../editor/hooks/useDocumentJobs.hook';
import { useModals } from '../../../hooks/useModals.hook';
import { AiProofreadModal } from '../../modals/aiProofread/aiProofreadModal.component';

export const JobList: React.FC<{
    document: DocumentV1.Record;
}> = ({ document }) => {
    const modals = useModals();
    const api = useApi();
    const queryClient = useQueryClient();
    const { jobList } = useDocumentJobs(document.documentId);

    const openJobDetails = (jobId: string) => {
        modals.showModal('ai-proofread', AiProofreadModal, {
            document: document,
            jobId: jobId
        });
    };

    const cancelJob = useMutation({
        mutationFn: async (jobId: string) => {
            await api.v1.documents.cancelJobById(document.documentId, jobId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documentJobs', document.documentId] });
        }
    });

    const dismissJob = useMutation({
        mutationFn: async (jobId: string) => {
            queryClient.setQueryData(
                [ 'documents', document.documentId, 'jobs' ],
                (old: JobQueueV1.List) => ({
                    ...old,
                    items: old.items.filter((item) => item.jobId !== jobId)
                })
            );

            await api.v1.documents.dismissJobById(document.documentId, jobId);
        }, 
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documentJobs', document.documentId] });
        }
    });

    return (
        <>
            {jobList.data?.items.map((item) => {
                return (
                    <div
                        key={item.jobId}
                        className={clsx(
                            'flex flex-row items-center gap-2 w-full mb-4 p-2 pl-4 rounded-full cursor-pointer',
                            'text-base font-medium',
                            item.status === JobQueueStatus.Pending && 'bg-blue-100',
                            item.status === JobQueueStatus.InProgress && 'border border-blue-300 bg-blue-100',
                            item.status === JobQueueStatus.Completed && 'border border-lime-500 bg-white',
                            item.status === JobQueueStatus.Failed && 'border border-red-500 bg-white',
                        )}
                        onClick={() => openJobDetails(item.jobId)}
                    >
                        {item.status === JobQueueStatus.Pending && (
                            <>
                                <ClockIcon className='text-slate-600' size={24} />
                                <span className='grow text-left text-sm'>Korektura čeká na zpracování ve frontě...</span>
                                <span className='px-4 py-2 rounded-full bg-primary-500 hover:bg-primary-550 text-sm text-white transition-colors duration-200'>Zobrazit postup</span>
                                <button type='button' className='px-4 py-2 rounded-full hover:bg-gray-300 text-sm text-gray-700 transition-colors duration-200' onClick={(e) => { e.stopPropagation(); cancelJob.mutate(item.jobId); }}>Zrušit</button>
                            </>
                        )}
                        {item.status === JobQueueStatus.InProgress && (
                            <>
                                <LoaderCircleIcon className='animate-spin text-primary-500' size={24} />
                                <span className='grow text-left text-sm'>Probíhá korektura dokumentu...</span>
                                <span className='px-4 py-2 rounded-full bg-primary-500 hover:bg-primary-550 text-sm text-white transition-colors duration-200'>Zobrazit postup</span>
                                <button type='button' className='px-4 py-2 rounded-full hover:bg-gray-300 text-sm text-gray-700 transition-colors duration-200' onClick={(e) => { e.stopPropagation(); cancelJob.mutate(item.jobId); }}>Zrušit</button>
                            </>
                        )}
                        {item.status === JobQueueStatus.Completed && (
                            <>
                                <CheckIcon className='text-lime-600' size={24} />
                                <span className='grow text-left text-sm'>Korektura dokumentu byla dokončena.</span>
                                <span className='px-4 py-2 rounded-full bg-lime-600 hover:bg-lime-700 text-sm text-white transition-colors duration-200'>Zobrazit výsledky</span>
                                <button type='button' className='px-4 py-2 rounded-full hover:bg-gray-300 text-sm text-gray-700 transition-colors duration-200' onClick={(e) => { e.stopPropagation(); dismissJob.mutate(item.jobId); }}>Zavřít</button>
                            </>
                        )}
                        {item.status === JobQueueStatus.Failed && (
                            <>
                                <AlertTriangleIcon className='text-red-600' size={24} />
                                <span className='grow text-left text-sm'>Při zpracování korektury došlo k chybě.</span>
                                <span className='px-4 py-2 rounded-full bg-red-600 hover:bg-red-700 text-sm text-white transition-colors duration-200'>Zobrazit detaily</span>
                                <button type='button' className='px-4 py-2 rounded-full hover:bg-gray-300 text-sm text-gray-700 transition-colors duration-200' onClick={(e) => { e.stopPropagation(); dismissJob.mutate(item.jobId); }}>Zavřít</button>
                            </>
                        )}
                    </div>
                );
            })}
        </>
    );
};
